/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import { Box, HStack, Icon, Text } from '@chakra-ui/core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { RiDragMove2Fill } from 'react-icons/ri';
import colors from '../../../../../styles/colors';
import { QuestionnaireTemplateStore } from '../../store/questionnaireTemplateModel';
import { SHARED_LAYOUT_TRANSITION } from '../styles/animations';
import { AnimatedWrapper } from './withLayoutAnimation';

interface DropZoneProps {
  parentId: string | null;
  branch: string | null;
  index: number;
  isInitial?: boolean;
}
const DEFAULT_VARIANT = { x: 0, transition: { height: { duration: 0.2, ease: 'easeOut' } } };
const ZONE_VARIANTS = {
  idle: { height: 0, ...DEFAULT_VARIANT },
  placeholder: { height: 182, ...DEFAULT_VARIANT },
  active: { height: 72, ...DEFAULT_VARIANT },
  blocked: {
    height: 72,
    x: [-4, 4, -4, 4, 0],
    transition: { ...DEFAULT_VARIANT.transition, x: { duration: 0.3 } },
  },
};

const BOX_BASE_PROPS = {
  borderRadius: 'md',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '60px',
  mb: 3,
};

const BlockedContent = React.memo(({ isMaxDepthLimit }: { isMaxDepthLimit: boolean }) => {
  const { t } = useTranslation('questionnaireTemplate');
  return (
    <HStack spacing={2}>
      <Icon name="warning" color="red.500" />
      <Text fontWeight="bold" color="red.600" fontSize="xs">
        {isMaxDepthLimit ? t('formBuilder.canvas.maxDepthError') : t('formBuilder.canvas.noDropError')}
      </Text>
    </HStack>
  );
});
BlockedContent.displayName = 'BlockedContent';

const ActiveContent = React.memo(() => {
  const { t } = useTranslation('questionnaireTemplate');

  return (
    <Text fontWeight="bold" color="blue.500" fontSize="xs">
      {t('formBuilder.canvas.dropMessage')}
    </Text>
  );
});
ActiveContent.displayName = 'ActiveContent';

const EmptyContent = React.memo(function EmptyContent() {
  const { t } = useTranslation('questionnaireTemplate');
  return (
    <HStack spacing={2}>
      <RiDragMove2Fill size="24px" color="rgba(88, 96, 115, 1)" />
      <Text fontSize="xs" fontWeight={700} lineHeight="21px" color="rgba(88, 96, 115, 1)">
        {t('formBuilder.canvas.emptyContent')}
      </Text>
    </HStack>
  );
});

export const DropZone = React.memo<DropZoneProps>(function DropZone({ parentId, branch, index, isInitial = false }) {
  const hoveredZoneSelector = React.useCallback(
    (state) => {
      const zone = state.ui.hoveredDropZone;
      if (!zone) return null;

      // eslint-disable-next-line
      const isMatch = zone.parentId === parentId && zone.branch === branch && zone.index === index;
      return isMatch ? { isBlocked: zone.isBlocked, isMaxDepthLimit: zone.isMaxDepthLimit } : null;
    },
    [parentId, branch, index],
  );

  // Get the currently dragging element info
  const draggingElementSelector = React.useCallback((state) => {
    const draggedId = state.ui.draggedElementId;
    if (!draggedId) return null;

    const element = state.entities.elements[draggedId];
    if (!element) return null;

    const dragKey = element.parentId ? `${element.parentId}-${element.parentBranch}` : 'root';
    const dragIndex = state.ordering[dragKey]?.indexOf(draggedId) ?? -1;

    return {
      parentId: element.parentId,
      branch: element.parentBranch,
      index: dragIndex,
    };
  }, []);

  const hoveredZone = QuestionnaireTemplateStore.useStoreState(hoveredZoneSelector);
  const draggingElement = QuestionnaireTemplateStore.useStoreState(draggingElementSelector);

  // Check if this dropzone is adjacent to the dragged element
  const isAdjacentDropZone = React.useMemo(() => {
    if (!draggingElement) return false;

    const isSameList = draggingElement.parentId === parentId && draggingElement.branch === branch;
    if (!isSameList) return false;

    // Hide dropzone directly below the dragged element (same index + 1)
    // eslint-disable-next-line
    return index === draggingElement.index || index === draggingElement.index + 1;
  }, [draggingElement, parentId, branch, index]);

  const isActive = hoveredZone !== null && !isAdjacentDropZone;
  const isMaxDepthLimit = hoveredZone?.isMaxDepthLimit ?? false;
  const isBlocked = hoveredZone?.isBlocked || isMaxDepthLimit;
  const isPlaceholder = index === 0 && isInitial;

  const currentVariant = React.useMemo(() => {
    if (!isActive) return 'idle';
    return isBlocked ? 'blocked' : 'active';
  }, [isActive, isBlocked]);

  const dropZoneData = React.useMemo(() => JSON.stringify({ parentId, branch, index }), [parentId, branch, index]);

  const boxBg = React.useMemo(() => {
    if (!isActive) return 'transparent';
    return isBlocked ? colors.red[50] : colors.blue[50];
  }, [isActive, isBlocked]);

  const borderColor = React.useMemo(() => {
    if (!isActive) return undefined;
    return isBlocked ? colors.red[400] : colors.blue[400];
  }, [isActive, isBlocked]);

  const border = React.useMemo(() => {
    if (!isActive && !isPlaceholder) return 'none';
    return '2px dashed';
  }, [isActive, isPlaceholder]);

  const containerHeight = React.useMemo(() => {
    if (isPlaceholder) return 182;
    return 60;
  }, [isPlaceholder]);

  return (
    <AnimatedWrapper
      data-dropzone={dropZoneData}
      motionProps={{
        variants: ZONE_VARIANTS,
        initial: 'idle',
        animate: isPlaceholder ? 'placeholder' : currentVariant,
        transition: SHARED_LAYOUT_TRANSITION,
      }}
      data-testid="dropzone-motion-wrapper"
    >
      <Box
        data-testid="dropzone"
        {...BOX_BASE_PROPS}
        bg={boxBg}
        border={border}
        borderColor={borderColor}
        height={`${containerHeight}px`}
      >
        {isPlaceholder ? (
          <EmptyContent />
        ) : (
          isActive && (isBlocked ? <BlockedContent isMaxDepthLimit={isMaxDepthLimit} /> : <ActiveContent />)
        )}
      </Box>
    </AnimatedWrapper>
  );
});
